import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { formatToSafeIso } from '@/lib/utils';

export interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video' | 'gif';
  thumbnail?: string;
}

export interface Comment {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string | null;
  content: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'gif';
  createdAt: string;
  reactions?: any[];
  replies?: Comment[];
}

export interface Reaction {
  id: string;
  userId: string;
  userName: string;
  type: 'like' | 'love' | 'laugh' | 'wow' | 'sad' | 'angry';
  timestamp: string;
}

export interface Location {
  lat: number;
  lng: number;
  address: string;
}

export interface LightboxData {
  media: (MediaItem | string | Record<string, any>)[];
  initialIndex?: number;
  backgroundAudioUrl?: string;
  autoPlayBackgroundAudio?: boolean;
  postId: string;
  authorId: string;
  authorName: string;
  authorPhoto: string | null;
  createdAt: string;
  content?: string;
  location?: Location | null;
  taggedUsers?: string[];
  reactions?: Reaction[];
  comments?: Comment[];
  onReactionAdd?: (type: string) => void;
  onCommentAdd?: (comment: string, mediaUrl?: string, mediaType?: string) => void;
  onLocationAdd?: (location: Location) => void;
  onTagUser?: (userId: string) => void;
  onUpdate?: (data: any) => void;
}

interface LightboxContextType {
  isOpen: boolean;
  lightboxData: LightboxData | null;
  openLightbox: (data: LightboxData) => void;
  closeLightbox: () => void;
}

const LightboxContext = createContext<LightboxContextType | undefined>(undefined);

export function LightboxProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [lightboxData, setLightboxData] = useState<LightboxData | null>(null);

  const openLightbox = useCallback((data: LightboxData) => {
    setLightboxData(data);
    setIsOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
    setLightboxData(null);
  }, []);

  const value = useMemo(
    () => ({ isOpen, lightboxData, openLightbox, closeLightbox }),
    [isOpen, lightboxData, openLightbox, closeLightbox]
  );

  return (
    <LightboxContext.Provider value={value}>
      {children}
    </LightboxContext.Provider>
  );
}

export function useLightbox() {
  const context = useContext(LightboxContext);
  if (context === undefined) {
    if (import.meta.env.DEV) {
      throw new Error('useLightbox must be used within a LightboxProvider');
    }

    return {
      isOpen: false,
      lightboxData: null,
      openLightbox: () => {
        console.warn('useLightbox called without LightboxProvider; openLightbox ignored.');
      },
      closeLightbox: () => {
        // no-op
      },
    };
  }
  return context;
}

// Helper hook for simple media lightbox
export function useMediaLightbox() {
  const { openLightbox } = useLightbox();

  const openMedia = useCallback((
    media: (MediaItem | string | Record<string, any>) | (MediaItem | string | Record<string, any>)[],
    options?: {
      initialIndex?: number;
      backgroundAudioUrl?: string;
      autoPlayBackgroundAudio?: boolean;
      postId?: string;
      authorId?: string;
      authorName?: string;
      authorPhoto?: string | null;
      createdAt?: string;
      content?: string;
      location?: Location | null;
      taggedUsers?: string[];
      reactions?: Reaction[];
      comments?: Comment[];
      onUpdate?: (data: any) => void;
    }
  ) => {
    const mediaArray = Array.isArray(media) ? media : [media];
    
    openLightbox({
      media: mediaArray,
      initialIndex: options?.initialIndex || 0,
      backgroundAudioUrl: options?.backgroundAudioUrl,
      autoPlayBackgroundAudio: options?.autoPlayBackgroundAudio,
      postId: options?.postId || 'temp-' + Date.now(),
      authorId: options?.authorId || '',
      authorName: options?.authorName || 'Unknown',
      authorPhoto: options?.authorPhoto || null,
      createdAt: formatToSafeIso(options?.createdAt),
      content: options?.content,
      location: options?.location,
      taggedUsers: options?.taggedUsers,
      reactions: options?.reactions,
      comments: options?.comments,
      onUpdate: options?.onUpdate,
    });
  }, [openLightbox]);

  return { openMedia };
}
